import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store/store';
import { initiateDeposit, fetchBalance } from '@/store/slices/walletSlice';
import { FiX, FiCreditCard, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { Currency } from '@/types/wallet';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useCurrency } from '@/contexts/CurrencyContext';
import { walletApi } from '@/services/api/wallet';

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type DepositStep = 'form' | 'processing' | 'verifying' | 'success' | 'error';

const DepositModal: React.FC<DepositModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { userCurrency } = useCurrency();

  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Currency>(userCurrency as Currency);
  const [step, setStep] = useState<DepositStep>('form');
  const [errorMessage, setErrorMessage] = useState('');
  const [successData, setSuccessData] = useState<any>(null);
  const [paymentUrl, setPaymentUrl] = useState<string>('');
  const [showPaymentWebview, setShowPaymentWebview] = useState(false);
  const [depositConfig, setDepositConfig] = useState<{ minDepositAmount: number; maxDepositAmount: number; currency?: string }>({
    minDepositAmount: 10,
    maxDepositAmount: 100000,
  });
  const [depositBaseCurrency, setDepositBaseCurrency] = useState<string>('');

  const currencies: Currency[] = ['GHS', 'USD', 'EUR', 'GBP'];

  useEffect(() => {
    const loadDepositConfig = async () => {
      try {
        const response = await walletApi.getDepositConfig(currency);
        const paystackConfig = response.data?.data?.walletDeposit?.paystack;
        if (paystackConfig) {
          setDepositConfig({
            minDepositAmount: paystackConfig.minDepositAmount || 10,
            maxDepositAmount: paystackConfig.maxDepositAmount || 100000,
            currency: paystackConfig.currency || currency,
          });
          setDepositBaseCurrency(paystackConfig.baseCurrency || paystackConfig.currency || '');
        }
      } catch {
        setDepositConfig({ minDepositAmount: 10, maxDepositAmount: 100000, currency });
        setDepositBaseCurrency('');
      }
    };

    if (isOpen) {
      loadDepositConfig();
    }
  }, [isOpen, currency]);

  //const paystackPublicKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || '';

  const handleReset = () => {
    setAmount('');
    setStep('form');
    setErrorMessage('');
    setSuccessData(null);
    setPaymentUrl('');
    setShowPaymentWebview(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleCloseWebview = () => {
    setShowPaymentWebview(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    if (parseFloat(amount) < depositConfig.minDepositAmount || parseFloat(amount) > depositConfig.maxDepositAmount) {
      setErrorMessage(
        `Deposit amount must be between ${depositConfig.minDepositAmount.toFixed(2)} and ${depositConfig.maxDepositAmount.toFixed(2)} ${currency}`
      );
      setStep('error');
      return;
    }

    if (!user?.email) {
      setErrorMessage('User email not found');
      setStep('error');
      return;
    }

    setStep('processing');
    setErrorMessage('');

    try {
      const depositData = {
        amount: parseFloat(amount),
        currency,
        method: 'paystack' as const,
      };

      const result = await dispatch(initiateDeposit(depositData));

      if (result.meta.requestStatus === 'fulfilled') {
        const paymentData = result.payload as any;
        
        if (!paymentData.authorizationUrl) {
          setErrorMessage('Failed to get payment URL');
          setStep('error');
          return;
        }

        setPaymentUrl(paymentData.authorizationUrl);
        setShowPaymentWebview(true);

        setStep('verifying');
        let pollCount = 0;
        const maxPolls = 30; // Poll for 5 minutes max
        
        const pollPayment = async () => {
          try {
            pollCount++;
            
            if (pollCount > maxPolls) {
              setErrorMessage('Payment verification timeout');
              setStep('error');
              return;
            }

            const verifyResult = await walletApi.verifyDeposit(paymentData.reference);
            
            if (verifyResult.data.success) {
              setShowPaymentWebview(false);
              setSuccessData(verifyResult.data.data);
              setStep('success');
              await dispatch(fetchBalance(currency));
            } else {
              setTimeout(pollPayment, 10000);
            }
          } catch (error: any) {
            if (pollCount > maxPolls) {
              setShowPaymentWebview(false);
              setErrorMessage(error?.response?.data?.message || 'Verification failed');
              setStep('error');
            } else {
              setTimeout(pollPayment, 10000);
            }
          }
        };

        // Start polling after a short delay to allow user to complete payment
        setTimeout(pollPayment, 5000);
      } else {
        setErrorMessage('Failed to initiate deposit');
        setStep('error');
      }
    } catch (error: any) {
      setErrorMessage(error?.message || 'An error occurred');
      setStep('error');
    }
  };

  if (!isOpen) return null;

  const renderContent = () => {
    switch (step) {
      case 'processing':
        return (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
              <LoadingSpinner size="md" />
            </div>
            <h3 className="mb-2 text-base font-semibold text-slate-900 dark:text-white sm:text-lg">
              Initializing Payment
            </h3>
            <p className="text-sm text-slate-600 dark:text-gray-400">
              Please wait while we prepare your payment...
            </p>
          </div>
        );

      case 'verifying':
        return (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-100 dark:bg-cyan-900/30 rounded-full mb-4">
              <LoadingSpinner size="md" />
            </div>
            <h3 className="mb-2 text-base font-semibold text-slate-900 dark:text-white sm:text-lg">
              Verifying Payment
            </h3>
            <p className="text-sm text-slate-600 dark:text-gray-400">
              Please wait while we confirm your payment...
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
              <FiCheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="mb-2 text-base font-semibold text-slate-900 dark:text-white sm:text-lg">
              Payment Successful!
            </h3>
            <p className="mb-4 text-sm text-slate-600 dark:text-gray-400">
              Your wallet has been credited successfully.
            </p>
            {successData && (
              <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left dark:border-gray-800 dark:bg-gray-900/50">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-gray-400">Amount:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {successData.amount} {successData.currency}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-gray-400">New Balance:</span>
                    <span className="font-bold text-green-600 dark:text-green-400">
                      {successData.balanceAfter} {successData.currency}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <button
              onClick={handleClose}
              className="w-full rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
            >
              Done
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
              <FiAlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="mb-2 text-base font-semibold text-slate-900 dark:text-white sm:text-lg">
              Payment Failed
            </h3>
            <p className="mb-6 text-sm text-slate-600 dark:text-gray-400">
              {errorMessage || 'An error occurred during the payment process.'}
            </p>
            <button
              onClick={handleReset}
              className="w-full rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        );

      default:
        return (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                Amount
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min={depositConfig.minDepositAmount}
                  max={depositConfig.maxDepositAmount}
                  required
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 pr-20 text-base text-slate-900 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as Currency)}
                  title="Select currency"
                  className="absolute right-2 top-1/2 -translate-y-1/2 border-l border-slate-300 bg-transparent px-2 py-1 font-medium text-slate-700 dark:border-gray-700 dark:text-gray-300"
                >
                  {currencies.map((curr) => (
                    <option key={curr} value={curr}>
                      {curr}
                    </option>
                  ))}
                </select>
              </div>
              <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">
                Minimum: {depositConfig.minDepositAmount.toFixed(2)} {currency} | Maximum: {depositConfig.maxDepositAmount.toFixed(2)} {currency}
              </p>
              {depositBaseCurrency && depositBaseCurrency !== currency && (
                <p className="mt-1 text-xs text-slate-500 dark:text-gray-500">
                  Limits converted from base currency {depositBaseCurrency} to {currency}
                </p>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
              <div className="flex items-center gap-3 mb-3">
                <div className="rounded-xl bg-primary-100 p-2 dark:bg-primary-900/30">
                  <FiCreditCard className="text-primary-600 dark:text-primary-400" size={20} />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white">
                    Paystack Payment Gateway
                  </h4>
                  <p className="text-xs text-slate-600 dark:text-gray-400">
                    Secure payment via Card, Bank Transfer, USSD & Mobile Money
                  </p>
                </div>
              </div>
              <div className="text-xs text-slate-500 dark:text-gray-400">
                A secure payment popup will open to complete your deposit.
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
              <h4 className="mb-2 font-medium text-slate-900 dark:text-white">
                Summary
              </h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-gray-400">Amount:</span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {amount ? parseFloat(amount).toFixed(2) : '0.00'} {currency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-gray-400">Deposit Policy:</span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    No fees apply
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2 dark:border-gray-800">
                  <span className="font-medium text-slate-900 dark:text-white">Total:</span>
                  <span className="font-bold text-slate-900 dark:text-white">
                    {amount ? parseFloat(amount).toFixed(2) : '0.00'} {currency}
                  </span>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={
                !amount ||
                parseFloat(amount) <= 0 ||
                parseFloat(amount) < depositConfig.minDepositAmount ||
                parseFloat(amount) > depositConfig.maxDepositAmount
              }
              title="Proceed to payment"
              className="w-full rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Proceed to Payment
            </button>
          </form>
        );
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-4">
        <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-gray-800 sm:p-5">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">
              {step === 'success' ? 'Deposit Successful' : step === 'error' ? 'Deposit Failed' : 'Deposit Funds'}
            </h2>
            <button
              onClick={handleClose}
              title="Close deposit modal"
              className="rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              disabled={step === 'processing' || step === 'verifying'}
            >
              <FiX size={24} />
            </button>
          </div>

          <div className="p-4 sm:p-5">{renderContent()}</div>
        </div>
      </div>

      {showPaymentWebview && paymentUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm sm:p-4">
          <div className="flex h-[80vh] w-full max-w-2xl flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-gray-800">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white sm:text-lg">
                Complete Payment
              </h3>
              <button
                onClick={handleCloseWebview}
                title="Close payment window"
                className="rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <FiX size={24} />
              </button>
            </div>
            <div className="flex-1 relative">
              <iframe
                src={paymentUrl}
                className="absolute inset-0 w-full h-full border-0"
                title="Payment Gateway"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
              />
            </div>
            <div className="border-t border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
              <p className="text-center text-sm text-slate-600 dark:text-gray-400">
                Complete your payment in the window above. This dialog will close automatically when payment is confirmed.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DepositModal;
